import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Gift, ExternalLink, CheckCircle, XCircle, Clock, Instagram, Youtube } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminEarnProReview() {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [reviewDialog, setReviewDialog] = useState({ open: false, submission: null, action: null });
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, [filter]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' 
        ? `${API}/earn-pro/admin/submissions`
        : `${API}/earn-pro/admin/submissions?status=${filter}`;
      
      const res = await fetch(url, { credentials: 'include' });
      
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const openReviewDialog = (submission, action) => {
    setReviewDialog({ open: true, submission, action });
    setAdminNotes('');
  };

  const handleReview = async () => {
    const { submission, action } = reviewDialog;
    
    setProcessing(true);
    try {
      const endpoint = action === 'approve' 
        ? `${API}/earn-pro/admin/approve/${submission.id}`
        : `${API}/earn-pro/admin/reject/${submission.id}`;
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          admin_notes: adminNotes || undefined,
          apply_coupon: action === 'approve'
        })
      });

      if (!res.ok) {
        throw new Error(`Failed to ${action} submission`);
      }

      const data = await res.json();
      toast.success(data.message);
      
      setReviewDialog({ open: false, submission: null, action: null });
      await fetchSubmissions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const config = {
      pending: { color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30', icon: Clock, label: 'Pending' },
      approved: { color: 'bg-green-500/10 text-green-500 border-green-500/30', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-500/10 text-red-500 border-red-500/30', icon: XCircle, label: 'Rejected' }
    };
    
    const { color, icon: Icon, label } = config[status] || config.pending;
    
    return (
      <Badge className={`${color} border px-3 py-1`}>
        <Icon className="h-3 w-3 mr-1" />
        {label}
      </Badge>
    );
  };

  const PlatformLink = ({ platform, url }) => {
    const icons = {
      tiktok: <FaTiktok className="h-4 w-4" />,
      instagram: <Instagram className="h-4 w-4" />,
      youtube: <Youtube className="h-4 w-4" />
    };

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
      >
        {icons[platform]}
        <span className="capitalize">{platform}</span>
        <ExternalLink className="h-3 w-3" />
      </a>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Gift className="h-8 w-8 text-blue-500" />
          <h1 className="text-3xl font-bold text-white">Earn Pro Month - Submissions</h1>
        </div>
        <p className="text-[#8A93A6]">
          Review and approve user submissions for free Pro month
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {['pending', 'approved', 'rejected', 'all'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            onClick={() => setFilter(status)}
            size="sm"
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Button>
        ))}
      </div>

      {/* Submissions List */}
      {submissions.length === 0 ? (
        <Card className="glass">
          <CardContent className="py-12 text-center">
            <p className="text-[#8A93A6]">No submissions found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => (
            <Card key={submission.id} className="glass">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-white mb-2">
                      {submission.user_name || 'Unknown User'}
                    </CardTitle>
                    <p className="text-sm text-[#8A93A6]">{submission.user_email}</p>
                    <p className="text-xs text-[#8A93A6] mt-1">
                      Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                  <StatusBadge status={submission.status} />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Platform Links */}
                <div>
                  <Label className="text-white mb-2 block">Content Links:</Label>
                  <div className="space-y-2">
                    {Object.entries(submission.platform_posts).map(([platform, url]) => (
                      <div key={platform} className="flex items-center gap-3">
                        <PlatformLink platform={platform} url={url} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* User Notes */}
                {submission.submission_notes && (
                  <div>
                    <Label className="text-white mb-2 block">User Notes:</Label>
                    <p className="text-sm text-[#8A93A6] p-3 rounded bg-white/5 border border-white/10">
                      {submission.submission_notes}
                    </p>
                  </div>
                )}

                {/* Admin Notes */}
                {submission.admin_notes && (
                  <div>
                    <Label className="text-white mb-2 block">Admin Notes:</Label>
                    <p className="text-sm text-[#8A93A6] p-3 rounded bg-white/5 border border-white/10">
                      {submission.admin_notes}
                    </p>
                  </div>
                )}

                {/* Coupon Status */}
                {submission.coupon_applied && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Coupon Applied
                  </Badge>
                )}

                {/* Action Buttons */}
                {submission.status === 'pending' && (
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => openReviewDialog(submission, 'approve')}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => openReviewDialog(submission, 'reject')}
                      variant="outline"
                      className="flex-1 border-red-500/30 hover:bg-red-500/10"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => !processing && setReviewDialog({ ...reviewDialog, open })}>
        <DialogContent className="bg-[#0A0F1E] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">
              {reviewDialog.action === 'approve' ? 'Approve Submission' : 'Reject Submission'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2">Admin Notes (Optional)</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder={
                  reviewDialog.action === 'approve'
                    ? 'Add any notes about the approval...'
                    : 'Explain why this submission is being rejected...'
                }
                rows={4}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            {reviewDialog.action === 'approve' && (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <p className="text-sm text-green-400">
                  ✓ Stripe coupon "EARN_PRO_MONTH" will be applied to this user's account
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewDialog({ open: false, submission: null, action: null })}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReview}
              disabled={processing}
              className={reviewDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {processing ? 'Processing...' : `Confirm ${reviewDialog.action === 'approve' ? 'Approval' : 'Rejection'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
